// External imports
import { client } from '@/lib/hono';

// Internal imports
import {
  Review,
  CreateReviewRequest,
  UpdateReviewRequest,
  ReviewWithUser,
  ReviewStats,
} from '@/types/reviews.types';

/**
 * Reviews API Service
 * Handles all review-related API operations
 */
export const reviewsService = {
  /**
   * Create a new review for a restaurant
   */
  async createReview(data: CreateReviewRequest): Promise<Review> {
    const response = await client.api.reviews.$post({
      json: data,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create review');
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Failed to create review');
    }

    return result.data;
  },

  /**
   * Get user's review for a specific restaurant
   */
  async getUserRestaurantReview(restaurantId: string): Promise<Review | null> {
    const response = await client.api.reviews.restaurant[':restaurantId'].user.$get({
      param: { restaurantId },
    });

    if (response.status === 404) {
      return null; // User hasn't reviewed this restaurant
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to get user review');
    }

    const result = await response.json();
    if (!result.success) {
      return null;
    }

    return result.data || null;
  },

  /**
   * Check if user has reviewed a restaurant and get the review if it exists
   */
  async checkUserReview(restaurantId: string): Promise<{ hasReviewed: boolean; userReview?: Review | null }> {
    const response = await client.api.reviews.restaurant[':restaurantId'].check.$get({
      param: { restaurantId },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to check review status');
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      return { hasReviewed: false };
    }

    return result.data;
  },

  /**
   * Update an existing review
   */
  async updateReview(reviewId: string, data: UpdateReviewRequest): Promise<Review> {
    const response = await client.api.reviews[':id'].$put({
      param: { id: reviewId },
      json: data,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to update review');
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Failed to update review');
    }

    return result.data;
  },

  /**
   * Delete a review
   */
  async deleteReview(reviewId: string): Promise<void> {
    const response = await client.api.reviews[':id'].$delete({
      param: { id: reviewId },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to delete review');
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error('Failed to delete review');
    }
  },

  /**
   * Get all reviews for a restaurant
   */
  async getRestaurantReviews(restaurantId: string): Promise<ReviewWithUser[]> {
    const response = await client.api.reviews.restaurant[':restaurantId'].$get({
      param: { restaurantId },
      query: {},
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to get restaurant reviews');
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error('Failed to get restaurant reviews');
    }

    return result.data || [];
  },

  /**
   * Get review statistics for a restaurant
   */
  async getRestaurantReviewStats(restaurantId: string): Promise<ReviewStats> {
    const response = await client.api.reviews.restaurant[':restaurantId'].stats.$get({
      param: { restaurantId },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to get review statistics');
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Failed to get review statistics');
    }

    return result.data;
  },

  /**
   * Get user's review statistics
   */
  async getUserReviewStats(): Promise<{ totalReviews: number; averageRating: number }> {
    const response = await client.api.reviews.stats.$get();

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to get user review statistics');
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Failed to get user review statistics');
    }

    return result.data;
  },

  /**
   * Get recent reviews across all restaurants
   */
  async getRecentReviews(limit: number = 10): Promise<any[]> {
    const response = await client.api.reviews.recent.$get({
      query: { limit: limit.toString() },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error((errorData as any).error || 'Failed to get recent reviews');
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error('Failed to get recent reviews');
    }

    return result.data || [];
  },
};